<template>
  <v-card class="mb-0 rounded-b-0">
    <div class="grid-scroll-wrapper">
      <table class="status-grid">
        <thead>
          <tr>
            <th class="type-col">
              Tipo de Ingresso
            </th>
            <th
              v-for="pos in posList"
              :key="pos.id"
              class="pos-col"
              :class="{ 'pos-col--online': pos.online_sales }"
            >
              {{ pos.name }}
              <div v-if="pos.online_sales && onlineSalesPeriod" class="pos-col-period">
                {{ onlineSalesPeriod }}
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          <template v-for="group in groupedTicketTypes" :key="group.sectorId">
            <tr class="sector-header-row">
              <td :colspan="posList.length + 1">
                {{ group.sectorName }}
              </td>
            </tr>
            <tr v-for="{ sectionName, type } in group.entries" :key="type.id">
              <td class="type-col type-col--label">
                <span class="text-caption text-medium-emphasis">{{ sectionName }} ›</span>
                {{ type.name }}
              </td>
              <td
                v-for="pos in posList"
                :key="pos.id"
                class="grid-cell"
                :class="{
                  'grid-cell--selected': isSelected(type.id, pos.id),
                  'grid-cell--online': pos.online_sales,
                  'grid-cell--disabled': isCellDisabled(type, pos),
                }"
                @click="!isCellDisabled(type, pos) && emit('selectChannel', { type, pos })"
              >
                <span v-if="!cellCurrentBatch(type.id, pos.id)" class="cell-empty">—</span>
                <template v-else>
                  <v-chip
                    :color="cellLabelColor(type.id, pos.id)"
                    size="x-small"
                    label
                    class="cell-chip mb-1"
                  >
                    {{ cellLabel(type.id, pos.id) }}
                  </v-chip>
                  <div class="cell-batch-detail">
                    <span class="cell-batch-number">L{{ cellCurrentBatch(type.id, pos.id)!.number }}</span>
                    <span class="cell-batch-price">{{ currency(cellCurrentBatch(type.id, pos.id)!.price) }}</span>
                    <span class="cell-batch-qty">{{ formatQuantity(cellCurrentBatch(type.id, pos.id)) }}</span>
                    <span class="cell-batch-period">
                      {{ formatPeriod(cellCurrentBatch(type.id, pos.id)) }}
                    </span>
                  </div>
                  <div class="cell-batch-upcoming">
                    <template v-if="cellNextBatch(type.id, pos.id)">
                      Próximo: L{{ cellNextBatch(type.id, pos.id)!.number }} —
                      {{ currency(cellNextBatch(type.id, pos.id)!.price) }} —
                      {{ formatQuantity(cellNextBatch(type.id, pos.id)) }} —
                      {{ formatPeriod(cellNextBatch(type.id, pos.id)) }}
                    </template>
                    <template v-else>
                      Próximo: n/a
                    </template>
                  </div>
                </template>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>
  </v-card>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { FreeTypes } from '@/constants/TicketTypeNames'
import { formatDate, formatPeriod, formatQuantity, isClosed, statusColor, statusLabel } from '@/helpers/batches'
import { currency } from '@/helpers/formatters/currency'
import type Event from '@/types/Event'
import type PointOfSale from '@/types/PointOfSale'
import type TicketBatch from '@/types/TicketBatch'
import type TicketType from '@/types/TicketType'

defineOptions({ name: 'BatchStatusGrid' })

interface Channel {
  type: TicketType
  pos: PointOfSale
}

const props = defineProps<{
  ticketTypes: TicketType[]
  posList: PointOfSale[]
  event: Event
  batchGrid: Record<string, Record<string, TicketBatch[]>>
  selectedChannel: Channel | null
}>()

const emit = defineEmits<{
  selectChannel: [channel: Channel]
}>()

const onlineSalesPeriod = computed<string>(() => {
  const { open, close } = props.event.dates.sales

  if (!open && !close) return ''

  return `${formatDate(open)} – ${formatDate(close)}`
})

// --- Grouping ---

interface GroupEntry {
  sectionName: string
  type: TicketType
}

interface SectorGroup {
  sectorId: string
  sectorName: string
  entries: GroupEntry[]
}

const groupedTicketTypes = computed<SectorGroup[]>(() => {
  const map = new Map<string, { sectorName: string, entries: GroupEntry[] }>()

  for (const type of props.ticketTypes) {
    if (!map.has(type.sector_id)) map.set(type.sector_id, { sectorName: type.sector_name, entries: [] })
    map.get(type.sector_id)!.entries.push({ sectionName: type.section_name, type })
  }

  return Array.from(map.entries()).map(([sectorId, { sectorName, entries } ]) => ({
    sectorId,
    sectorName,
    entries,
  }))
})

// --- Cell helpers ---

function cellBatches(typeId: string, posId: string): TicketBatch[] {
  return props.batchGrid[typeId]?.[posId] ?? []
}

function cellCurrentBatch(typeId: string, posId: string): TicketBatch | null {
  const batches = cellBatches(typeId, posId)

  const active = batches.find(b => b.status === 'active')

  if (active) return active

  const scheduled = batches
    .filter(b => b.status === 'scheduled')
    .sort((a, b) => (a.starts_at ?? '').localeCompare(b.starts_at ?? ''))

  if (scheduled.length > 0) return scheduled[0]

  const past = batches
    .filter(isClosed)
    .sort((a, b) => (b.ends_at ?? '').localeCompare(a.ends_at ?? ''))

  if (past.length > 0) return past[0]

  return null
}

/**
 * The successor of the cell's current batch.
 */
function cellNextBatch(typeId: string, posId: string): TicketBatch | null {
  const current = cellCurrentBatch(typeId, posId)

  if (!current) return null

  return cellBatches(typeId, posId)
    .filter(b => b.number > current.number)
    .sort((a, b) => a.number - b.number)[0] ?? null
}

function cellLabel(typeId: string, posId: string): string {
  return statusLabel(cellCurrentBatch(typeId, posId))
}

function cellLabelColor(typeId: string, posId: string): string {
  return statusColor(cellCurrentBatch(typeId, posId))
}

function isSelected(typeId: string, posId: string): boolean {
  return props.selectedChannel?.type.id === typeId && props.selectedChannel?.pos.id === posId
}

function isCellDisabled(type: TicketType, pos: PointOfSale): boolean {
  return pos.name === 'Especiais' && !FreeTypes.includes(type.name)
}

</script>

<style scoped>
.grid-scroll-wrapper {
  overflow-x: auto;
}

.status-grid {
  width: 100%;
  border-collapse: collapse;
}

.status-grid thead {
  position: sticky;
  top: 0;
  z-index: 2;
  background: rgb(var(--v-theme-surface));
}

.status-grid thead th {
  padding: 10px 12px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: thin solid rgba(var(--v-border-color), var(--v-border-opacity));
  white-space: nowrap;
}

.pos-col--online {
  border-left: 2px solid rgb(var(--v-theme-primary));
  border-right: 2px solid rgb(var(--v-theme-primary));
}

.pos-col-period {
  margin-top: 2px;
  font-size: 0.65rem;
  font-weight: 400;
  text-transform: none;
  letter-spacing: 0;
  opacity: 0.6;
}

.sector-header-row td {
  padding: 6px 12px;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: rgba(var(--v-theme-on-surface), 0.5);
  background: rgba(var(--v-theme-on-surface), 0.03);
}

.type-col {
  min-width: 180px;
  text-align: left;
}

.type-col--label {
  padding: 6px 12px;
  font-size: 0.875rem;
}

.pos-col {
  min-width: 160px;
  text-align: center;
}

.grid-cell {
  padding: 6px 10px;
  text-align: left;
  cursor: pointer;
  border-bottom: thin solid rgba(var(--v-border-color), var(--v-border-opacity));
  transition: background 0.1s;
}

.grid-cell:hover {
  background: rgba(var(--v-theme-primary), 0.05);
}

.grid-cell--selected {
  background: rgba(var(--v-theme-primary), 0.1) !important;
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: -2px;
}

.grid-cell--disabled {
  opacity: 0.25;
  cursor: not-allowed;
  pointer-events: none;
}

.grid-cell--online {
  border-left: 2px solid rgb(var(--v-theme-primary));
  border-right: 2px solid rgb(var(--v-theme-primary));
}

.cell-empty {
  color: rgba(var(--v-theme-on-surface), 0.3);
  font-size: 0.875rem;
}

.cell-chip {
  cursor: pointer;
}

.cell-batch-detail {
  display: flex;
  flex-direction: column;
  gap: 1px;
  margin-top: 2px;
}

.cell-batch-number,
.cell-batch-price,
.cell-batch-qty,
.cell-batch-period {
  font-size: 0.6rem;
  opacity: 0.75;
  white-space: nowrap;
}

.cell-batch-number {
  font-weight: 600;
  opacity: 0.5;
}

.cell-batch-price {
  font-weight: 600;
  opacity: 0.9;
}

.cell-batch-upcoming {
  margin-top: 3px;
  padding-top: 3px;
  border-top: thin dashed rgba(var(--v-border-color), var(--v-border-opacity));
  font-size: 0.6rem;
  font-style: italic;
  opacity: 0.6;
  white-space: nowrap;
}
</style>
